import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Lock, Zap } from "lucide-react";
import { PHASES, SKILL_LEVELS, BETA_MODE } from "./GrindConstants";

const GrindDayTracker = ({ progress, completedDays, skillLevel }) => {
  const savedSkillLevel = skillLevel || 'beginner';
  const skillConfig = SKILL_LEVELS[savedSkillLevel];
  const startWeek = skillConfig?.startWeek || 1;

  const getPhaseColor = (phase) => {
    switch (phase.color) {
      case 'blue': return 'from-blue-500/20 to-blue-500/5 border-blue-500/30';
      case 'purple': return 'from-purple-500/20 to-purple-500/5 border-purple-500/30';
      case 'orange': return 'from-orange-500/20 to-orange-500/5 border-orange-500/30';
      case 'green': return 'from-green-500/20 to-green-500/5 border-green-500/30';
      default: return 'from-primary/20 to-primary/5 border-primary/30';
    }
  };

  const renderWeekGrid = () => {
    const weeks = [];
    for (let week = startWeek; week <= 23; week++) {
      const days = [];
      const phase = PHASES.find(p => week >= p.week[0] && week <= p.week[1]);
      for (let dayOfWeek = 1; dayOfWeek <= 7; dayOfWeek++) {
        const dayNumber = (week - startWeek) * 7 + dayOfWeek;
        const isCompleted = completedDays.has(dayNumber);
        const isCurrent = progress?.current_day === dayNumber;
        const isFuture = dayNumber > (progress?.current_day || 0);
        const isRestDay = dayOfWeek === 7;
        days.push(
          <div
            key={`${week}-${dayOfWeek}`}
            className={`w-8 h-8 rounded-sm flex items-center justify-center text-xs font-bold transition-all ${
              isCompleted ? 'bg-green-500 text-white'
                : isCurrent ? 'bg-primary text-white ring-2 ring-primary ring-offset-2 ring-offset-background'
                : isRestDay ? 'bg-purple-500/20 text-purple-400'
                : isFuture ? 'bg-muted/30 text-muted-foreground'
                : 'bg-red-500/20 text-red-400'
            }`}
            title={`Day ${dayNumber}${isRestDay ? ' (Rest)' : ''} - Week ${week}`}
          >
            {isCompleted ? <CheckCircle className="w-4 h-4" /> : dayNumber}
          </div>
        );
      }
      weeks.push(
        <div key={week} className="flex items-center gap-1">
          <div className={`w-16 text-xs ${week < 5 && savedSkillLevel === 'advanced' ? 'text-muted-foreground/30 line-through' : 'text-muted-foreground'}`}>
            Week {week}
          </div>
          <div className="flex gap-1">{days}</div>
          <div className={`ml-2 w-2 h-2 rounded-full ${phase ? `bg-${phase.color}-500` : 'bg-gray-500'}`} />
          {week === startWeek && savedSkillLevel === 'advanced' && (
            <Badge className="ml-2 bg-red-500/20 text-red-400 text-xs">Start</Badge>
          )}
        </div>
      );
    }
    return weeks;
  };

  return (
    <>
      {/* Week Grid */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-heading text-white">Your Journey</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 overflow-x-auto">
            {renderWeekGrid()}
          </div>
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-border">
            {PHASES.map((phase) => (
              <div key={phase.name} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full bg-${phase.color}-500`} />
                <span className="text-xs text-muted-foreground">{phase.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Phase Breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        {PHASES.map((phase) => {
          const startDay = (phase.week[0] - 1) * 7 + 1;
          const endDay = phase.week[1] * 7;
          const daysInPhase = Array.from({ length: endDay - startDay + 1 }, (_, i) => startDay + i);
          const completedInPhase = daysInPhase.filter(d => completedDays.has(d)).length;
          const isActive = progress?.current_day >= startDay && progress?.current_day <= endDay;
          const isCompleted = progress?.current_day > endDay;
          const isUnlocked = BETA_MODE || isActive || isCompleted;
          const PhaseIcon = phase.icon;
          return (
            <Card 
              key={phase.name}
              className={`bg-gradient-to-r ${getPhaseColor(phase)} border ${isActive ? 'ring-2 ring-primary' : ''} ${BETA_MODE && !isActive && !isCompleted ? 'opacity-90' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <PhaseIcon className={`w-5 h-5 text-${phase.color}-500`} />
                      <h3 className="font-bold text-white">{phase.name}</h3>
                      {BETA_MODE && !isActive && !isCompleted && (
                        <Badge className="bg-cyan-500/20 text-cyan-400 text-[10px]">BETA</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">Weeks {phase.week[0]}-{phase.week[1]}</p>
                    <p className="text-xs text-muted-foreground mt-1">{phase.focus}</p>
                  </div>
                  <div className="text-right">
                    {isCompleted ? (
                      <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" />Complete</Badge>
                    ) : isActive ? (
                      <Badge className="bg-primary/20 text-primary">Active</Badge>
                    ) : BETA_MODE ? (
                      <Badge className="bg-cyan-500/20 text-cyan-400"><Zap className="w-3 h-3 mr-1" />Unlocked</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground"><Lock className="w-3 h-3 mr-1" />Locked</Badge>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">{completedInPhase}/{daysInPhase.length} days</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};

export default GrindDayTracker;
